import { useRef } from "react";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";

const CASES = [
  { company: "Global CPG Leader", sub: "Reinventing the month-end close", quote: "Agents reconcile millions of transactions across 12 ERPs and surface only the exceptions that need a human.", m: [["45%", "Faster Close"], ["12", "ERPs Unified"]], tags: ["Reconciliation", "Finance"], feat: true },
  { company: "Fortune 100 Bank", sub: "Trusted context for finance ops", quote: "A shared semantic layer means every team works from the same governed numbers.", m: [["3\u00D7", "Faster Recon"], ["100%", "Governed"]], tags: ["Semantic Layer", "BFSI"] },
  { company: "Industrial Manufacturer", sub: "Smarter supply planning", quote: "Agentic planning balances service and working capital in real time.", m: [["20%", "Lower Inventory"], ["Live", "In Prod"]], tags: ["Planning", "Supply Chain"] },
  { company: "Healthcare Network", sub: "Always-current reporting", quote: "Static dashboards gave way to decision-ready intelligence for operators.", m: [["60%", "Less Manual"], ["Live", "24/7"]], tags: ["Operations", "AI Over BI"] },
  { company: "Global Retail Group", sub: "Enterprise-wide transformation", quote: "From data products to embedded agents, Intugle reshaped how the whole enterprise makes decisions.", m: [["2\u00D7", "Throughput"], ["9", "Functions Live"]], tags: ["Marketing", "Enterprise"], feat: true },
  { company: "Telecom Provider", sub: "From cases to resolution", quote: "Agents triage, diagnose and resolve across systems automatically.", m: [["35%", "Faster Resolve"], ["Live", "CX"]], tags: ["Service", "Agents"] },
  { company: "Energy Enterprise", sub: "An enterprise context layer", quote: "Fragmented data became governed, reusable products powering every agent.", m: [["40+", "Sources"], ["1", "Source of Truth"]], tags: ["Data Products"] },
  { company: "Global Tech Firm", sub: "Reimagining the lifecycle", quote: "Onboarding is coordinated across HR, IT and Finance without manual chasing.", m: [["50%", "Faster Onboarding"], ["Live", "HR"]], tags: ["HR", "People Ops"] },
];

const Tags = ({ tags, dark }) => (
  <div className="mt-auto pt-4 flex flex-wrap gap-2">{tags.map((t) => <span key={t} className={`text-[11px] font-semibold rounded-md px-2.5 py-1 ${dark ? "bg-[#8A5730]/40 text-[#F0D9BE]" : "bg-[#8A5730]/10 text-[#8A5730]"}`}>{t}</span>)}</div>
);
const Metrics = ({ m, dark }) => (
  <div className="mt-5 flex gap-8">{m.map(([n, l]) => <div key={l}><div className={`font-display text-2xl font-medium ${dark ? "text-[#E8AA68]" : "text-[#8A5730]"}`}>{n}</div><div className={`text-[10.5px] uppercase tracking-[0.12em] mt-0.5 ${dark ? "text-white/55" : "text-[#3E2818]/50"}`}>{l}</div></div>)}</div>
);

/* Variant 1 · Spotlight dark (same size, "Flagship" pill) */
const Card1 = ({ c }) => c.feat ? (
  <div className="shrink-0 w-[320px] sm:w-[360px] snap-start rounded-2xl bg-gradient-to-b from-[#2E1B0C] to-[#160D05] text-[#F7F5F0] p-6 flex flex-col ring-1 ring-[#E8AA68]/40">
    <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-[#E8AA68] text-[#211307] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] mb-4"><Star size={11} className="fill-[#211307]" /> Flagship</span>
    <h4 className="font-display text-2xl font-medium text-white">{c.company}</h4>
    <p className="text-[12.5px] text-white/60 mt-1">{c.sub}</p>
    <p className="text-[13.5px] italic text-white/85 leading-relaxed mt-4">{c.quote}</p>
    <Metrics m={c.m} dark /><Tags tags={c.tags} dark />
  </div>
) : (
  <div className="shrink-0 w-[300px] sm:w-[320px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 p-6 flex flex-col">
    <h4 className="font-display text-xl font-medium text-[#3E2818]">{c.company}</h4>
    <p className="text-[12px] text-[#3E2818]/55 mt-1">{c.sub}</p>
    <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-4">{c.quote}</p>
    <Metrics m={c.m} /><Tags tags={c.tags} />
  </div>
);

/* Variant 2 · Featured wider + dark */
const Card2 = ({ c }) => c.feat ? (
  <div className="shrink-0 w-[400px] sm:w-[460px] snap-start rounded-2xl bg-gradient-to-br from-[#3E2818] to-[#160D05] text-[#F7F5F0] p-7 flex flex-col">
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">Transformational Impact</span>
    <h4 className="font-display text-3xl font-medium text-white">{c.company}</h4>
    <p className="text-[13px] text-white/60 mt-1">{c.sub}</p>
    <p className="text-[15px] italic text-white/90 leading-relaxed mt-4 flex-1">{c.quote}</p>
    <Metrics m={c.m} dark /><Tags tags={c.tags} dark />
  </div>
) : (
  <div className="shrink-0 w-[280px] sm:w-[300px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 p-6 flex flex-col">
    <h4 className="font-display text-xl font-medium text-[#3E2818]">{c.company}</h4>
    <p className="text-[12px] text-[#3E2818]/55 mt-1">{c.sub}</p>
    <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-4">{c.quote}</p>
    <Metrics m={c.m} /><Tags tags={c.tags} />
  </div>
);

/* Variant 3 · Gold-glow featured (white card, gold ring + accent) */
const Card3 = ({ c }) => c.feat ? (
  <div className="shrink-0 w-[320px] sm:w-[350px] snap-start rounded-2xl p-[2px] bg-gradient-to-b from-[#EDA13A] to-[#8A5730]">
    <div className="rounded-[14px] bg-white h-full p-6 flex flex-col">
      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#B87A32] mb-3">Flagship Result</span>
      <h4 className="font-display text-2xl font-medium text-[#3E2818]">{c.company}</h4>
      <p className="text-[12.5px] text-[#3E2818]/55 mt-1">{c.sub}</p>
      <p className="text-[13.5px] text-[#3E2818]/75 leading-relaxed mt-4 flex-1">{c.quote}</p>
      <Metrics m={c.m} /><Tags tags={c.tags} />
    </div>
  </div>
) : (
  <div className="shrink-0 w-[300px] sm:w-[320px] snap-start rounded-2xl bg-white border border-[#3E2818]/10 p-6 flex flex-col opacity-95">
    <h4 className="font-display text-xl font-medium text-[#3E2818]">{c.company}</h4>
    <p className="text-[12px] text-[#3E2818]/55 mt-1">{c.sub}</p>
    <p className="text-[13px] text-[#3E2818]/70 leading-relaxed mt-4">{c.quote}</p>
    <Metrics m={c.m} /><Tags tags={c.tags} />
  </div>
);

const Carousel = ({ Card }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 380, behavior: "smooth" });
  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Powering Enterprises of All Sizes</p>
          <h2 className="font-display text-3xl lg:text-[2.75rem] font-medium tracking-tight text-[#3E2818] leading-[1.05]">Agents <span className="italic">in production</span><br />not just demos.</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowLeft size={17} /></button>
          <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-[#3E2818]/25 flex items-center justify-center text-[#3E2818] hover:bg-[#211307] hover:text-[#F7F5F0] hover:border-[#211307] transition-colors"><ArrowRight size={17} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-1 px-1 items-stretch [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {CASES.map((c, i) => <Card key={i} c={c} />)}
      </div>
    </div>
  );
};

const OPTIONS = [
  { id: "j", title: "Option J · Spotlight Dark (same size)", note: "The 2 flagship stories become dark espresso cards with a gold \u201CFlagship\u201D pill and gold ring, sitting inline with the white cards. Clear emphasis, tidy rhythm.", Card: Card1 },
  { id: "k", title: "Option K · Featured Wider + Dark", note: "Flagships are both dark AND wider than the rest, with a \u201CTransformational Impact\u201D label and larger type. Maximum standout for your two hero stories.", Card: Card2 },
  { id: "l", title: "Option L · Gold-Glow Featured", note: "Keeps everything light: flagships get a gold gradient ring and a \u201CFlagship Result\u201D label while others stay plain white. Subtle, premium emphasis.", Card: Card3 },
];

export default function CaseStudyOptions6() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Success Stories · Flagship Emphasis</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">2 flagship stories standing out among 8</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">8 cards; the 1st and 5th are the flagship, transformational stories. Scroll or use the arrows. Tell me the letter.</p>

        <div className="mt-12 space-y-20">
          {OPTIONS.map((o) => (
            <div key={o.id}>
              <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#B87A32] mb-6">{o.title}</p>
              <Carousel Card={o.Card} />
              <p className="text-[12px] text-[#3E2818]/55 mt-4 leading-snug max-w-3xl">{o.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
